import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { GlobalI18n } from '@settings/global-i18n';
import { Module } from '@shared/models/module-name';
import { MasterDataService } from '@shared/data-provider/master-data-api/api/masterData.service';
import { AircraftKind } from '@shared/data-provider/master-data-api/model/aircraftKind';
import { NzModalService } from 'ng-zorro-antd/modal';
import { MasterDataFunctionsService } from '../../services/master-data.service';
import { CustomFormValidators } from '@shared/custom-form-validators';
import { map } from 'rxjs';
import { convertDateToUTC } from '@shared/functions/dateFunctions';
import { SettingsService } from '@core/services/settings/settings.service';

interface ExtendedAircraftKind extends AircraftKind {
	nameEn?: string;
	nameRu?: string;
	nameLocal?: string;
	dtRangeStart?: Date;
	dtRangeFinish?: Date;
}

@Component({
	selector: 'app-ac-kinds',
	templateUrl: './ac-kinds.component.html',
	styleUrls: ['./ac-kinds.component.less'],
})
export class AcKindsComponent implements OnInit {
	constructor(
		private apiMasterData: MasterDataService,
		private settingsService: SettingsService,
		private modal: NzModalService,
		private fb: UntypedFormBuilder,
		private globalI18n: GlobalI18n,
		private functionsService: MasterDataFunctionsService
	) {}

	private formValidators: CustomFormValidators = new CustomFormValidators();

	private _kinds: ExtendedAircraftKind[] = [];
	kind: ExtendedAircraftKind;
	kindForm: FormGroup;

	settingValue = {
		loadingList: false,
		saving: false,
		searchName: '',
		isVisibleModal: false,
		loadedPercent: null,
		pageIndex: 1,
	};

	listOfColumn = [];

	ngOnInit() {
		this.loadAircraftKinds();
		this.listOfColumn = [
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnName'),
				compare: (a: ExtendedAircraftKind, b: ExtendedAircraftKind) => a.nameLocal.localeCompare(b.nameLocal),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnLastUpdate'),
				compare: (a: ExtendedAircraftKind, b: ExtendedAircraftKind) =>
					Date.parse(a.lastupdate) - Date.parse(b.lastupdate),
				priority: false,
			},
		];
	}

	loadAircraftKinds() {
		this.settingValue.loadingList = true;
		this.apiMasterData.selectRecords('aircraft_kinds').subscribe((data: AircraftKind[]) => {
			this._kinds = data.map(item => {
				return {
					...item,
					nameEn: item.name[0],
					nameRu: item.name[1],
					nameLocal: this.settingsService.language == 'EN' ? item.name[0] : item.name[1],
					dtRangeStart: new Date(item.dtRange[0]),
					dtRangeFinish: new Date(item.dtRange[1]),
				};
			});
			this.settingValue.loadingList = false;
		});
	}

	get listOfDisplayKinds(): ExtendedAircraftKind[] {
		// При установленных фильтрах необходимо сбросить пагинацию на 1 страницу
		if (this.settingValue.searchName.length !== 0) {
			this.onPageIndexChanged(1);
		}
		return this._kinds.filter(item => {
			return (
				item.nameRu.toLowerCase().indexOf(this.settingValue.searchName.toLowerCase()) != -1 ||
				item.nameEn.toLowerCase().indexOf(this.settingValue.searchName.toLowerCase()) != -1
			);
		});
	}

	openAircraftKind(id: number) {
		this.settingValue.loadingList = true;
		return this.apiMasterData
			.selectRecord('aircraft_kinds', id.toString())
			.pipe(
				map((item: AircraftKind) => {
					return {
						...item,
						nameEn: item.name[0],
						nameRu: item.name[1],
						nameLocal: this.settingsService.language == 'EN' ? item.name[0] : item.name[1],
						dtRangeStart: new Date(item.dtRange[0]),
						dtRangeFinish: new Date(item.dtRange[1]),
					};
				})
			)
			.subscribe((data: ExtendedAircraftKind) => {
				this.kind = data;
				this.kindForm = this.createKindForm(data);
				this.showModal();
				this.settingValue.loadingList = false;
			});
	}

	addKind() {
		this.kind = {};
		this.kindForm = this.createKindForm();
		this.showModal();
	}

	createKindForm(data?: ExtendedAircraftKind) {
		return this.fb.group({
			nameEn: [data ? data.nameEn : '', [Validators.required, this.formValidators.onlyEnglish()]],
			nameRu: [data ? data.nameRu : '', [Validators.required]],
			dtRangeStart: [data ? convertDateToUTC(new Date(data.dtRangeStart)) : new Date(), [Validators.required]],
			dtRangeFinish: [
				data ? convertDateToUTC(new Date(data.dtRangeFinish)) : new Date('2099-12-31 23:59'),
				[Validators.required],
			],
		});
	}

	add() {
		this.kindForm.markAllAsTouched();
		if (this.kindForm.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.insertRecord('aircraft_kinds', null, this.createKindFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadAircraftKinds();
				},
				error: err => {
					this.displayError(err);
				},
				complete: () => {
					this.settingValue.saving = false;
				},
			});
		}
	}

	save() {
		this.kindForm.markAllAsTouched();
		if (this.kindForm.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.updateRecord('aircraft_kinds', this.kind.id.toString(), this.createKindFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadAircraftKinds();
				},
				error: err => this.displayError(err),
				complete: () => {
					this.settingValue.saving = true;
				},
			});
		}
	}

	delete() {
		this.settingValue.saving = true;
		this.kindForm.get('dtRangeFinish').setValue(new Date());
		this.apiMasterData.updateRecord('aircraft_types', this.kind.id.toString(), this.createKindFromForm()).subscribe({
			next: () => {
				this.closeModal();
				this.loadAircraftKinds();
			},
			error: err => this.displayError(err),
			complete: () => {
				this.settingValue.saving = true;
			},
		});
	}

	duplicate() {
		this.kind.id = undefined;
		this.kind.lastupdate = undefined;
	}

	showDeleteConfirm(): void {
		this.modal.confirm({
			nzTitle: this.globalI18n.getMessage(Module.MasterData, 'deleteItem'),
			nzOkText: this.globalI18n.getMessage(Module.MasterData, 'yes'),
			nzOkType: 'primary',
			nzOkDanger: true,
			nzOnOk: () => this.delete(),
			nzCancelText: this.globalI18n.getMessage(Module.MasterData, 'no'),
			nzOnCancel: () => console.log('Cancel'),
		});
	}

	createKindFromForm(): AircraftKind {
		return {
			...this.kind,
			...this.kindForm.value,
			name: [this.kindForm.get('nameEn').value, this.kindForm.get('nameRu').value],
			dtRange: [this.kindForm.get('dtRangeStart').value, this.kindForm.get('dtRangeFinish').value],
		};
	}

	downloadCsvFile() {
		this.functionsService.downloadCsvFile('aircraft_kinds', this.settingValue.loadingList);
	}

	downloadXmlFile() {
		this.functionsService.downloadXmlFile('aircraft_kinds', this.settingValue.loadingList);
	}

	onPageIndexChanged(index: number) {
		this.settingValue.pageIndex = index;
	}

	showModal(): void {
		this.settingValue.isVisibleModal = true;
	}

	closeModal(): void {
		this.settingValue.isVisibleModal = false;
	}

	displayError(err): void {
		this.modal.create({
			nzTitle: 'Error',
			nzContent: err.message,
			nzClosable: false,
		});
	}
}
